import React from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
} from 'recharts';
import { DATA_LAYERS } from '../../../lib/dataLayers';
import { useDeforestationAlerts } from '../../../hooks/useDeforestationAlerts';
import { useForestOwnership } from '../../../hooks/useForestOwnership';
import { LucaMonthlyPoint } from '../../../lib/types';
import {
  CHART_TOOLTIP_STYLE,
  CHART_AXIS_STYLE,
  CHART_GRID_STROKE,
  CHART_AXIS_STROKE,
} from '../../../lib/chartStyles';
import { LayerHeader } from './LayerHeader';
import { StatCard } from './StatCard';
import { AboutLayer } from './AboutLayer';
import { GlossaryTerm } from '../../shared/GlossaryTerm';
import {
  LayerLoadingLabel,
  LayerErrorAlert,
  SkeletonPulseBlock,
  SkeletonStatCardGrid,
} from './contentPrimitives';

interface DeforestationAlertsContentProps {
  millCode: string | null;
  driveTimeMinutes: number;
}

const BAR_COLOR = '#F97316';       // orange-500 — monthly values
const TREND_COLOR = '#7DD3FC';     // sky-300 — 12-mo rolling mean, cool contrast to orange
const YEAR_BAND_COLOR = '#FFFFFF'; // rendered at very low opacity for alternating bands
const ROLLING_WINDOW = 12;

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const fmtHectares = (n: number): string =>
  `${Math.round(n).toLocaleString('en-US')} ha`;

// FO totals are precomputed in CONUS Albers acres; LUCA is in hectares. Use
// the standard conversion to bring them into the same units before composing
// the "% of forested area" stat.
const ACRES_PER_HECTARE = 2.4710538;

const fmtMonthYear = (isoDate: string): string => {
  const [y, m] = isoDate.split('-');
  const idx = Number(m) - 1;
  if (idx < 0 || idx > 11) return isoDate;
  return `${MONTH_NAMES[idx]} ${y}`;
};

interface ChartPoint extends LucaMonthlyPoint {
  // Trailing 12-month mean of areaHa (inclusive of current month).
  // null until ROLLING_WINDOW points are available, which tells recharts
  // to leave a gap at the start of the trend line rather than drawing a
  // misleading curve over a partial window.
  rolling: number | null;
}

const addRollingMean = (points: LucaMonthlyPoint[]): ChartPoint[] =>
  points.map((p, i) => {
    if (i < ROLLING_WINDOW - 1) return { ...p, rolling: null };
    const slice = points.slice(i - ROLLING_WINDOW + 1, i + 1);
    const mean = slice.reduce((s, q) => s + q.areaHa, 0) / slice.length;
    return { ...p, rolling: mean };
  });

interface YearBand {
  x1: string;
  x2: string;
}

// One band per even-indexed year (0-indexed across the years present), used
// as ultra-low-opacity ReferenceAreas to subtly separate years without the
// visual noise of vertical gridlines.
const computeYearBands = (points: LucaMonthlyPoint[]): YearBand[] => {
  const years = Array.from(new Set(points.map(p => p.year))).sort();
  return years
    .map((y, idx) => {
      if (idx % 2 !== 0) return null;
      const months = points.filter(p => p.year === y);
      if (months.length === 0) return null;
      return { x1: months[0].date, x2: months[months.length - 1].date };
    })
    .filter((b): b is YearBand => b !== null);
};

interface Summary {
  totalHa: number;
  last3Ha: number;
  monthlyAvgLast3: number;
  peak: { date: string; areaHa: number } | null;
}

const computeSummary = (points: LucaMonthlyPoint[]): Summary => {
  const totalHa = points.reduce((s, p) => s + p.areaHa, 0);
  const last3 = points.slice(-3);
  const last3Ha = last3.reduce((s, p) => s + p.areaHa, 0);
  const monthlyAvgLast3 = last3.length > 0 ? last3Ha / last3.length : 0;

  let peak: Summary['peak'] = null;
  for (const p of points) {
    if (!peak || p.areaHa > peak.areaHa) peak = { date: p.date, areaHa: p.areaHa };
  }

  return { totalHa, last3Ha, monthlyAvgLast3, peak };
};

const DeforestationSkeleton: React.FC = () => (
  <div className="space-y-4">
    <LayerLoadingLabel layer={DATA_LAYERS.deforestation_alerts} />
    <SkeletonPulseBlock className="h-[260px]" />
    <SkeletonStatCardGrid />
  </div>
);

export const DeforestationAlertsContent: React.FC<DeforestationAlertsContentProps> = ({
  millCode,
  driveTimeMinutes,
}) => {
  const layer = DATA_LAYERS.deforestation_alerts;
  const { data, isLoading, error } = useDeforestationAlerts(millCode, driveTimeMinutes);
  // Forest-area context for Greg's "scale relative to total forest cover" ask.
  // We don't gate the LUCA chart on this — if FO is slow / errors, the chart
  // still renders; only the "% of forested area" stat falls back to "—".
  const fo = useForestOwnership(millCode, driveTimeMinutes);
  const forestedAreaHa = fo.data
    ? fo.data.forestAcres / ACRES_PER_HECTARE
    : null;

  return (
    <div className="space-y-6">
      <LayerHeader layer={layer} />

      <AboutLayer>
        <p>
          <GlossaryTerm term="luca">LUCA</GlossaryTerm> (Land Use Change Alerts)
          is a CTrees satellite-monitoring product flagging recent tree-cover
          loss in near real time. Values shown here are the total area
          (hectares) of new alerts each month within the supply shed.
        </p>
        <p>
          <span className="font-semibold text-figma-text">
            Disturbance vs. deforestation.
          </span>{' '}
          The IPCC (and the EU Deforestation Regulation, EUDR) defines{' '}
          <span className="italic">deforestation</span> as{' '}
          <span className="italic">
            the conversion of forest land to non-forest land use
          </span>
          . LUCA detects tree-cover loss but doesn&rsquo;t attribute the
          land-use change, so the values here are reported as forest{' '}
          <span className="italic">disturbance</span> &mdash; a superset that
          includes harvest, fire, insect outbreaks, and true deforestation.
        </p>
      </AboutLayer>

      {isLoading && <DeforestationSkeleton />}

      {!isLoading && error && (
        <LayerErrorAlert layer={layer} details={error} />
      )}

      {!isLoading && data && data.points.length > 0 && (() => {
        const summary = computeSummary(data.points);
        const chartData = addRollingMean(data.points);
        const yearBands = computeYearBands(data.points);
        // Only tick at January of each year so the x-axis reads as year
        // boundaries; feeding explicit ticks avoids recharts collapsing them.
        const yearTicks = chartData.filter(p => p.month === 1).map(p => p.date);
        const firstDate = data.points[0].date;
        const lastDate = data.points[data.points.length - 1].date;

        return (
          <>
            <div className="flex items-center gap-2 text-xs font-roboto-mono">
              <span className="text-green-400/70">LUCA data from CTrees</span>
              <span className="text-neutral-600">·</span>
              <a
                href="https://ctrees.org/luca"
                target="_blank"
                rel="noopener noreferrer"
                className="text-figma-lime hover:underline"
              >
                ctrees.org/luca
              </a>
            </div>

            <div className="bg-figma-card rounded-figma-panel p-4 border border-figma-lime/30 shadow-figma-card">
              <div className="flex items-baseline justify-between mb-1">
                <h4 className="text-base font-roboto font-bold text-figma-text">
                  Monthly Disturbance <span className="text-neutral-500 font-normal">(hectares)</span>
                </h4>
                <div className="flex items-center gap-4 text-[10px] font-roboto-mono uppercase tracking-wider text-neutral-400">
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-2 h-2 rounded-sm" style={{ background: BAR_COLOR }} />
                    Monthly
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block w-3 h-[2px]" style={{ background: TREND_COLOR }} />
                    12-mo avg
                  </span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={260} minWidth={0}>
                <ComposedChart
                  data={chartData}
                  margin={{ top: 8, right: 12, left: 8, bottom: 5 }}
                  barCategoryGap={1}
                >
                  {/* Alternating low-opacity year bands replace vertical
                      gridlines — subtler and easier to read at a glance. */}
                  {yearBands.map(b => (
                    <ReferenceArea
                      key={b.x1}
                      x1={b.x1}
                      x2={b.x2}
                      fill={YEAR_BAND_COLOR}
                      fillOpacity={0.025}
                      stroke="none"
                      ifOverflow="hidden"
                    />
                  ))}
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={CHART_GRID_STROKE}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    stroke={CHART_AXIS_STROKE}
                    style={CHART_AXIS_STYLE}
                    tickMargin={6}
                    ticks={yearTicks}
                    tickFormatter={(v: string) => v.slice(0, 4)}
                  />
                  <YAxis
                    stroke={CHART_AXIS_STROKE}
                    style={CHART_AXIS_STYLE}
                    tickFormatter={(v: number) =>
                      v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v.toFixed(0)
                    }
                    width={48}
                  />
                  <Tooltip
                    contentStyle={CHART_TOOLTIP_STYLE}
                    formatter={(value: number | undefined, name) => {
                      const label = name === 'rolling' ? '12-mo avg' : 'Monthly';
                      return [value != null ? fmtHectares(value) : '—', label];
                    }}
                    labelFormatter={(label) => fmtMonthYear(String(label))}
                  />
                  <Bar
                    dataKey="areaHa"
                    fill={BAR_COLOR}
                    fillOpacity={0.85}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="rolling"
                    stroke={TREND_COLOR}
                    strokeWidth={2}
                    dot={false}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
              <p className="text-xs font-roboto italic text-neutral-500 mt-2 leading-relaxed">
                Monthly LUCA disturbance alerts within the supply shed, from{' '}
                {fmtMonthYear(firstDate)} through {fmtMonthYear(lastDate)}. The
                trend line is a trailing 12-month mean; it starts once a full
                year of data is available.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <StatCard
                label="Forested area (shed)"
                value={
                  forestedAreaHa != null ? fmtHectares(forestedAreaHa) : '—'
                }
                info="Total forested acres within the supply shed (USFS RDS-2025-0045 ownership raster, all seven forest classes summed). Provided as denominator context for the disturbance totals — converted from acres at 2.471 acres/ha."
              />
              <StatCard
                label="% of forested area"
                value={
                  forestedAreaHa != null && forestedAreaHa > 0
                    ? `${((summary.totalHa / forestedAreaHa) * 100).toFixed(2)}%`
                    : '—'
                }
                info={`Cumulative disturbance area (${fmtMonthYear(firstDate)}–${fmtMonthYear(lastDate)}) divided by the shed's total forested area. Like the Total, this is gross loss — not adjusted for regrowth — so the same hectare disturbed in two separate years counts twice.`}
              />
              <StatCard
                label={`Total (${fmtMonthYear(firstDate)}–${fmtMonthYear(lastDate)})`}
                value={fmtHectares(summary.totalHa)}
                info="Cumulative gross loss area summed across every month in the window. Not adjusted for regrowth — if 500 ha are disturbed and 200 ha later recover, the Total still reads 500 ha. Track the 12-month trend line on the chart for a smoother read."
              />
              <StatCard
                label="Last 3 months"
                value={fmtHectares(summary.last3Ha)}
              />
              <StatCard
                label="Monthly avg (last 3)"
                value={fmtHectares(summary.monthlyAvgLast3)}
                tooltipPlacement="top"
              />
              <StatCard
                label="Peak month"
                value={
                  summary.peak
                    ? `${fmtHectares(summary.peak.areaHa)}`
                    : '—'
                }
                info={summary.peak ? `Occurred in ${fmtMonthYear(summary.peak.date)}` : undefined}
                tooltipPlacement="top"
              />
            </div>
          </>
        );
      })()}

      {!isLoading && !error && data && data.points.length === 0 && (
        <div className="bg-figma-card rounded-figma-panel p-5 border border-figma-lime/20">
          <p className="text-sm font-roboto text-neutral-400">
            No disturbance alerts recorded for this supply shed.
          </p>
        </div>
      )}
    </div>
  );
};
